import { Fragment, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import { cn } from "@/components/utils/utils";
import { DashboardItinerary } from "@/lib/models/dashboard";
import {
  useItineraryActionItems,
  type ItineraryActionHandlers,
  type ItineraryActionItem,
} from "./useItineraryActionItems";

interface ItineraryActionsProps extends ItineraryActionHandlers {
  itinerary: DashboardItinerary;
  variant?: "card" | "table";
  onOpenChange?: (open: boolean) => void;
  onPdfStateChange?: (isGenerating: boolean, isSuccess: boolean) => void;
}

/** Menu a tre puntini di un itinerario (tabella e card). Le voci: useItineraryActionItems. */
export const ItineraryActions = ({
  variant = "card",
  onOpenChange,
  ...options
}: ItineraryActionsProps) => {
  const groups = useItineraryActionItems(options);

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild onClick={stopPropagation}>
        {variant === "card" ? (
          <Button
            variant="ghost"
            size="sm"
            className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white/95"
            title="Menu"
          >
            <MoreHorizontal className="h-5 w-5 text-gray-800" />
          </Button>
        ) : (
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {groups.map((group, index) => (
          <Fragment key={group[0]?.id ?? index}>
            {index > 0 && <DropdownMenuSeparator />}
            {group.map((item) => (
              <DropdownMenuItem
                key={item.id}
                disabled={item.disabled}
                className={cn("gap-2", item.destructive && "text-red-600 focus:text-red-600")}
                onClick={(e) => {
                  e.stopPropagation();
                  item.onSelect();
                }}
              >
                {item.icon}
                {item.label}
              </DropdownMenuItem>
            ))}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface ItineraryActionButtonsProps extends ItineraryActionHandlers {
  itinerary: DashboardItinerary;
  /**
   * Chiamata prima di un'azione che porta altrove (apre il builder, un'altra modale,
   * cancella): chi ospita i pulsanti in una modale la chiude. Non scatta per PDF e
   * download offline, che mostrano l'avanzamento sul pulsante stesso.
   */
  onLeave?: () => void;
  /** Voci da non mostrare: es. 'open', quando l'apertura e' gia' il pulsante principale. */
  exclude?: string[];
  /** Voci secondarie: finiscono in un menu a tre puntini in coda invece che come pulsanti. */
  overflow?: string[];
  /**
   * Pulsante principale (apri / modifica): seconda riga, a tutta larghezza, con accanto
   * i tre puntini delle voci secondarie.
   */
  primary?: ReactNode;
  className?: string;
}

/** Le stesse azioni del menu, come pulsanti: per l'anteprima dell'itinerario. */
export const ItineraryActionButtons = ({
  onLeave,
  exclude = [],
  overflow = [],
  primary,
  className,
  ...options
}: ItineraryActionButtonsProps) => {
  const groups = useItineraryActionItems(options);
  const items = groups.flat().filter((item) => !exclude.includes(item.id));

  const visible = items.filter((item) => !overflow.includes(item.id));
  const hidden = items.filter((item) => overflow.includes(item.id));

  const run = (item: ItineraryActionItem) => {
    if (!item.keepsContext) onLeave?.();
    item.onSelect();
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {/* Prima riga: le azioni in vista, a larghezza uguale */}
      {visible.length > 0 && (
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${visible.length}, minmax(0, 1fr))` }}
        >
          {visible.map((item) => (
            <Button
              key={item.id}
              variant="outline"
              size="sm"
              disabled={item.disabled}
              className={cn(
                "w-full gap-2 min-w-0",
                item.destructive && "border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              )}
              onClick={() => run(item)}
            >
              {item.icon}
              <span className="truncate">{item.label}</span>
            </Button>
          ))}
        </div>
      )}

      {/* Seconda riga: il pulsante principale e i tre puntini con le secondarie */}
      {(primary || hidden.length > 0) && (
        <div className="flex gap-2">
          {primary && <div className="flex-1 min-w-0">{primary}</div>}
          {hidden.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="lg" className="w-12 shrink-0 px-0" title="Menu">
                  <MoreHorizontal className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              {/* Si apre verso l'alto: il pulsante sta in fondo alla modale.
                  z-[100], come il Popover: la modale e' a z-[60]+, con lo z-50 di
                  default il menu si aprirebbe sotto. */}
              <DropdownMenuContent side="top" align="end" className="w-52 z-[100]">
                {hidden.map((item, index) => (
                  <Fragment key={item.id}>
                    {/* Le azioni distruttive in fondo, staccate dalle altre */}
                    {item.destructive && index > 0 && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      disabled={item.disabled}
                      className={cn("gap-2", item.destructive && "text-red-600 focus:text-red-600")}
                      onClick={() => run(item)}
                    >
                      {item.icon}
                      {item.label}
                    </DropdownMenuItem>
                  </Fragment>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
    </div>
  );
};
